import { _fetch } from 'jsx/common/util/_fetch';
import {
  APIAsset,
  ApiObjectPermissions,
  AssetPermissionTypes,
  BaseObjects,
  ContentTypeAPI,
  EnrichedObjectPermissions,
  ObjectGroups,
  ObjectPermission,
  RemoteAccessProfile,
} from '@mss/mss-component-library';

export const emptyObjectGroups: ObjectGroups = { assets: [], applications: [] };

export const mapObjectToGenericType = (obj: APIAsset) => {
  const assets = ['dcs_plc', 'field', 'compute', 'network', 'env'];
  const applications = [
    'application',
    'asset_management',
    'analyzer_management',
    'security',
    'control',
    'virtualization',
  ];

  if (assets.includes(obj.type)) return 'assets';
  if (applications.includes(obj.type)) return 'applications';
  throw new Error('Cannot map object to generic group type');
};

export const groupObjectByType = (
  objects: APIAsset[],
  existingIds: string[]
): ObjectGroups => {
  const groups = emptyObjectGroups;
  objects.forEach(obj => {
    if (existingIds.includes(obj.id!)) return;
    if (
      groups[mapObjectToGenericType(obj)].findIndex(x => x.id === obj.id) > -1
    )
      return;
    groups[mapObjectToGenericType(obj)].push(obj);
  });

  return groups;
};

export const contentTypeToObjectType = (
  type: string
): { endpoints: (id: string) => string[]; type: string } => {
  // TODO: Since it's only assets & applications, only 1 API endpoint is used for fetching detail information about that asset.
  // TODO: In a later state there needs to be endpoint mapping according to content_type_models
  switch (true) {
    case type.includes('profile'):
      return {
        endpoints: id => [`/api/remote-profile/${id}/`],
        type: 'profile',
      };
    case type.includes('asset'):
    default:
      return {
        endpoints: id => [
          `/api/assets/?id=${id}`,
          `/api/assets/${id}/profiles/`,
        ],
        type: 'asset',
      };
  }
};

export const serializeToObjectIds = (data: ApiObjectPermissions) => {
  const res: BaseObjects = {};

  Object.entries(data).forEach(
    ([key, permissions]: [string, { [_: string]: ObjectPermission }]) => {
      const first: string = Object.keys(permissions)[0];
      const objType = contentTypeToObjectType(
        permissions[first].content_type_model
      );

      if (res[objType.type]) return res[objType.type].entries.push(key);
      res[objType.type] = { entries: [key], urls: objType.endpoints };
    }
  );

  return res;
};

export const enrichObjectPermissionSets = async (
  objects: BaseObjects,
  data: ApiObjectPermissions
) => {
  const assetsResp = await Promise.all(
    objects['asset']?.entries.map(y =>
      Promise.all(objects['asset'].urls(y).map(url => _fetch(url)))
    ) || []
  );
  const profilesResp = await Promise.all(
    objects['profile']?.entries.map(y =>
      Promise.all(objects['profile'].urls(y).map(url => _fetch(url)))
    ) || []
  );
  // Rough way of typecasting
  const enrichedData: EnrichedObjectPermissions = { ...data } as any;
  const assets = await Promise.all(
    assetsResp.map(respArr => Promise.all(respArr.map(r => r.json())))
  );
  const profiles = await Promise.all(
    profilesResp.map(respArr => Promise.all(respArr.map(r => r.json())))
  );

  assets.forEach(a => {
    const [[object_data], profiles] = a;
    if (!object_data) return;
    const entries = Object.keys(data[object_data.id]).map(
      key => data[object_data.id][key]
    );
    enrichedData[object_data.id] = {
      entries,
      object_data,
      profiles,
      isNew: false,
      // @ts-ignore TODO: Fix this after ISAE PR is merged with new scoped comp lib
      hasInheritedPermissions: entries.some(entry => entry.groups?.length > 0),
    };
  });

  profiles.forEach(async ([p]) => {
    if (enrichedData[p.asset]) {
      enrichedData[p.asset] = {
        ...enrichedData[p.asset],
        entries: [
          ...(enrichedData[p.asset].entries || []),
          ...(Object.entries(data[p.id]).map(x => x[1]) || []),
        ],
      };
    }
  });
  return enrichedData;
};

export const unsavedToObjPerms = async (
  arr: { [key: string]: APIAsset[] },
  existingPermissions: ApiObjectPermissions,
  permissionProfiles?: boolean
) => {
  const keys = Object.keys(arr);
  const flat: APIAsset[] = keys.map(k => arr[k]).flat();
  if (flat.length === 0) return undefined;

  const profilesResp = await Promise.all(
    flat.map(a => ({
      id: a.id!,
      profiles:
        // @ts-ignore
        a.profile_count > 0 && permissionProfiles
          ? _fetch(`/api/assets/${a.id}/profiles/`)
          : undefined,
    }))
  );

  const profiles: {
    id: string;
    profiles: Array<RemoteAccessProfile & { id: string }>;
  }[] = await Promise.all(
    profilesResp.map(async p => ({
      ...p,
      profiles: await p.profiles?.then(x => x.json() || []),
    }))
  );

  const obj: EnrichedObjectPermissions = {};
  flat.forEach(f => {
    const matchedProfiles = profiles.find(p => p.id === f.id);
    const entries = matchedProfiles?.profiles
      ? matchedProfiles.profiles
          .map(mp => {
            if (existingPermissions[mp.id])
              return Object.entries(existingPermissions[mp.id]).map(x => x[1]);
            return [];
          })
          .flat()
      : [];

    return (obj[f.id!] = {
      profiles: matchedProfiles?.profiles || [],
      entries: entries || [],
      object_data: f,
      isNew: true,
    });
  });
  return obj;
};

const objContentType: { [key: string]: AssetPermissionTypes } = {
  windows_agentless: 'computeassetwindowsagentless',
  fcs: 'dcsplcassetfcs',
  scs: 'dcsplcassetscs',
  wac: 'dcsplcassetwac',
  bcv: 'dcsplcassetbcv',
  avr: 'dcsplcassetavr',
  fcn: 'dcsplcassetfcn',
  hmi_stn: 'dcsplcassethmistn',
  field: 'fieldassetprm',
  network_firewall: 'networkassetfirewall',
  network_router: 'networkassetrouter',
  network_switch: 'networkassetswitch',
  network_timeserver: 'networkassettimeserver',
  wsus: 'securityapplicationwsus',
  trellix_epo: 'securityapplicationtrellixepo',
  veeam: 'securityapplicationveeam',
  claroty: 'securityapplicationclaroty',
  nozomi: 'securityapplicationnozomi',
  whatsup_gold: 'securityapplicationwhatsupgold',
  odu: 'envassetodu',
  prm: 'assetmanagementapplicationprm',
  isae: 'assetmanagementapplicationisae',
  aaims: 'analyzermanagementapplicationaaims',
  calsys: 'analyzermanagementapplicationcalsys',
  microsoft_hyper_v: 'virtualizationapplicationmicrosofthyperv',
  centum_vp: 'controlapplicationcentumvp',
};

/** Model name for permission checks (e.g. change_dcsplcassetfcs). Used by bulk operations. */
export const getModelNameForCoreAsset = (
  coreAssetName: string
): string | undefined => objContentType[coreAssetName];

export const objectTypeToContentType = (obj: any) => {
  if (obj != null) {
    const { type } = obj;
    switch (type) {
      case 'compute':
        return objContentType[obj.compute_type];
      case 'field':
        return objContentType[obj.type];
      case 'dcs_plc':
        return objContentType[obj.dcs_plc_type];
      case 'network':
        return objContentType[`network_${obj.network_type}`];
      case 'application':
        return objContentType[obj.application_type];
      case 'env':
        return objContentType[obj.env_type];
      case 'asset_management':
        return objContentType[obj.assetmanagement_type];
      case 'analyzer_management':
        return objContentType[obj.analyzermanagement_type];
      case 'control':
        return objContentType[obj.control_type];
      default:
        throw new Error(`Could not map object type: ${type} to a content type`);
    }
  }
};

export const getContentType = (ctName: string, ct: ContentTypeAPI[]) => {
  if (!ctName)
    throw new Error('Could not convert object type to a content type');
  const res = ct.find(x => x.model === ctName);
  if (!res) throw new Error('Could not convert object type to a content type');
  return res;
};
